import { getIndexesOfInputsInOutputs } from 'pages/ConfigurePage/ConfigureForm/helpers';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppMode } from 'store/Common/reducers/common';
import { setModel } from 'store/Model/actions/model';
import { SET_TRIGGER_QUERY_REQUEST_AFTER_FORM_RESET } from 'store/constants';
import { IRootReducerState } from 'store/store';
import { ITestCompanyModelConfiguration } from 'types/Configuration.types';
import { IQuestion, QuestionOrigin, QuestionType } from 'types/Question.types';
import { havePricingValuesChanged } from './helpers';
import { RootState } from 'store/store';
import { useTemplateConfigureParams } from 'utils/hooks/useTemplateConfigureParams';

export const useFormatFormValuesIntoQueryPayload = (
  values: object,
  questionInitialKeysToExcludeInQueryRef: MutableRefObject<string[]>,
  // The answers as the last query response left them, used to tell an answer
  // the user has just changed from one that came back unchanged.
  valuesFromOutputs: object
) => {
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );
  // Company id is extracted from the intent when using DealerPanel or Blueprint app modes
  const shouldIncludeCompanyId = [AppMode.BUYER, AppMode.SUPPLIER].includes(
    appMode
  );
  const { companyId: orderCompanyId } = useSelector(
    (state: RootState) => state.orderInfo
  );

  // Optional chain because `company` is genuinely nullable: an admin has no
  // selected company, and the intent-authenticated modes have none until the
  // intent resolves. Only the SUPPLIER branch below reads it, so undefined here
  // never reaches the payload.
  const loggedInCompanyId = useSelector(
    (state: IRootReducerState) => state.userInfo.company?.id
  );

  const urlCompanyId = useGetCompanyIdFromUrl();

  // Use buyer company id for Supplier mode and null for Buyer mode
  const payload: {
    questions: Record<
      string,
      { values: unknown[]; indicative?: boolean; origin?: QuestionOrigin }
    >;
    company_id?: number | null;
    company_mode?: AppMode;
  } = {
    questions: {},
    // An explicit companyId in the URL wins: that is the admin panel tab, where
    // the company is named rather than inferred, and company_mode does not apply.
    ...(urlCompanyId !== undefined
      ? { company_id: urlCompanyId }
      : shouldIncludeCompanyId && {
          company_id:
            appMode === AppMode.SUPPLIER
              ? orderCompanyId
                ? Number(orderCompanyId)
                : Number(loggedInCompanyId)
              : null,
          ...(appMode === AppMode.BUYER && { company_mode: appMode }),
        }),
  };

  const questions: IQuestion[] = useSelector(
    (state: IRootReducerState) => state.modelInfo.questions
  );

  // initialKeys of questions the user marked as indicative (quotation mode).
  // Read here so the payload recomputes on toggle; the flag then rides along on
  // the next query and on the add-to-cart/submit payload.
  const indicativeQuestionKeys = useSelector(
    (state: IRootReducerState) =>
      state.submitConfigurationInfo.indicativeQuestionKeys ?? []
  );

  for (const valuesKey of Object.keys(values)) {
    const question = questions.find(
      (question) => question.initialKey === valuesKey
    );

    if (
      !question ||
      questionInitialKeysToExcludeInQueryRef.current.includes(
        question?.initialKey
      )
    ) {
      continue;
    }

    // An answer that is not there is sent as an explicit empty one rather than
    // left out of the payload. `[null]` is the shape the backend's own
    // emptiness check looks for; an empty `values` array is rejected by the
    // request schema, which is what a cleared multi select would otherwise
    // serialise to.
    //
    // No origin rides along: the backend reads any empty answer as unanswered,
    // whether the user emptied it or it was never filled in, and answers a
    // required question with a single valid option by itself either way.
    if (isClearedAnswer(values[valuesKey])) {
      payload.questions[question.initialKey] = { values: [null] };
      continue;
    }

    switch (question.type) {
      case QuestionType.BOOLEAN:
        payload.questions[question.initialKey] = {
          values: [values[valuesKey]],
        };
        break;
      case QuestionType.NUMBER:
        payload.questions[question.initialKey] = {
          values: [
            values[valuesKey] !== null
              ? Number(values[valuesKey])
              : values[valuesKey],
          ],
        };
        break;
      case QuestionType.TABLE: {
        const tablePayloadValues = formatTablePayloadValues(
          question,
          values[valuesKey]
        );
        if (tablePayloadValues.length) {
          payload.questions[question.initialKey] = {
            values: tablePayloadValues,
          };
        }
        break;
      }
      case QuestionType.LIST:
        payload.questions[question.initialKey] = {
          values: Array.isArray(values[valuesKey])
            ? values[valuesKey]
            : [values[valuesKey]],
        };
        break;
      case QuestionType.TEXT:
        payload.questions[question.initialKey] = {
          values: [values[valuesKey]],
        };
        break;
      case QuestionType.RADIO:
        payload.questions[question.initialKey] = {
          values: [values[valuesKey]],
        };
        break;
      case QuestionType.COLOR: {
        payload.questions[question.initialKey] = {
          values: [values[valuesKey]],
        };
        break;
      }
    }

    // Only attach when true so the order-flow payload stays byte-identical
    // (indicativeQuestionKeys is always empty outside quotation mode).
    if (
      payload.questions[question.initialKey] &&
      indicativeQuestionKeys.includes(question.initialKey)
    ) {
      payload.questions[question.initialKey].indicative = true;
    }

    // The backend fills in a required question by itself once one valid
    // option is left, and marks that answer "auto". Echoing that back is what
    // lets it drop the answer again when the options widen: an answer that
    // arrives without an origin counts as the user's own choice and is kept
    // as long as it stays valid.
    //
    // So the origin is only echoed while the answer is still the one the
    // response came back with. The moment the user picks a value themselves
    // the answer stops being automatic and the origin is left off, which is
    // how the backend reads a deliberate choice. Without that the value would
    // be re-derived on every query - the same value while it is the only
    // option, but silently dropped once the options widen.
    if (
      payload.questions[question.initialKey] &&
      question.origin === QuestionOrigin.AUTO &&
      JSON.stringify(values[valuesKey]) ===
        JSON.stringify(valuesFromOutputs[question.initialKey])
    ) {
      payload.questions[question.initialKey].origin = QuestionOrigin.AUTO;
    }
  }

  return payload;
};

// An answer reaches the payload as undefined for the types that clear to no
// value at all (list, radio, colour, table), as null for text and number, and
// as an empty array for the multi selects, whose Formik value is the list of
// picked options. False and 0 are answers, not empties.
const isClearedAnswer = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (Array.isArray(value) && !value.length);

export const formatTablePayloadValues = (
  question: IQuestion,
  tableValues: any
) => {
  const formattedTableValues: any[] = [];
  const indexesOfInputsInOutputs: number[] = getIndexesOfInputsInOutputs(
    question.inputs,
    question.outputs
  );
  for (const tableValue of tableValues) {
    const formattedTableValue = [];
    for (const tableValueElementIndex in tableValue) {
      if (indexesOfInputsInOutputs.includes(Number(tableValueElementIndex)))
        formattedTableValue.push(tableValue[tableValueElementIndex]);
    }
    formattedTableValues.push(formattedTableValue);
  }
  return formattedTableValues;
};

export const useValuesDidUpdate = (
  values: any,
  currentModelId: any,
  testConfiguration: ITestCompanyModelConfiguration | null,
  taxRate: number | undefined,
  includeBomItems: boolean,
  pricing: Record<string, any>,
  customNetPrice: number | null
): boolean => {
  const dispatch = useDispatch();
  const triggerQueryRequestAfterFormReset = useSelector(
    (state: IRootReducerState) =>
      state.modelInfo.triggerQueryRequestAfterFormReset
  );
  const prevValuesRef = useRef<any>(null);
  const prevTestConfigurationRef = useRef<any>(null);
  const prevTaxRateRef = useRef<any>(null);
  const prevModelIdRef = useRef<any>(null);
  const prevIncludeBomItemsRef = useRef<any>(null);
  const prevPricingRef = useRef<any>(null);
  const prevCustomNetPriceRef = useRef<any>(null);

  // Check only changes of custom_amount and custom_discount
  const didPricingValuesChange = havePricingValuesChanged(
    prevPricingRef.current,
    pricing
  );

  if (triggerQueryRequestAfterFormReset) {
    dispatch({
      type: SET_TRIGGER_QUERY_REQUEST_AFTER_FORM_RESET,
      payload: false,
    });
  }

  const didValuesChange =
    currentModelId !== prevModelIdRef.current ||
    JSON.stringify(values) !== JSON.stringify(prevValuesRef.current) ||
    JSON.stringify(testConfiguration) !==
      JSON.stringify(prevTestConfigurationRef.current) ||
    JSON.stringify(taxRate) !== JSON.stringify(prevTaxRateRef.current) ||
    includeBomItems !== prevIncludeBomItemsRef.current ||
    JSON.stringify(customNetPrice) !==
      JSON.stringify(prevCustomNetPriceRef.current) ||
    didPricingValuesChange ||
    triggerQueryRequestAfterFormReset;

  if (didValuesChange) {
    prevValuesRef.current = values;
    prevTestConfigurationRef.current = testConfiguration;
    prevTaxRateRef.current = taxRate;
    prevModelIdRef.current = currentModelId;
    prevIncludeBomItemsRef.current = includeBomItems;
    prevPricingRef.current = pricing;
    prevCustomNetPriceRef.current = customNetPrice;
  }

  return didValuesChange;
};

export const useIsEditMode = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isEditModeString = queryParams.get('isEdit');
  return isEditModeString === 'true';
};

export const useIntentConfiguration = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const intentConfigurationString = queryParams.get('intentConfiguration');
  return intentConfigurationString === 'true';
};

export const getModelIdFromPath = () => {
  const queryParams = new URLSearchParams(location.search);
  return queryParams.get('modelId');
};

export const useModelIdChange = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const { productId } = useSelector((state: RootState) => state.orderInfo);

  // Refs to store the previous modelId
  const prevModelIdRef = useRef<string | null>(null);

  // State to store the current modelId
  const [currentModelId, setCurrentModelId] = useState<string | null>(null);

  // State to store the comparison result
  const [hasModelIdChanged, setHasModelIdChanged] = useState<boolean>(false);

  useEffect(() => {
    const modelId = getModelIdFromPath();
    if (!modelId) {
      setCurrentModelId(productId); // ID from redux for order
    } else {
      setCurrentModelId(modelId); // ID from url for model editor
    }
    // setCurrentModelId(modelId); // Update the current modelId

    // Compare the previous modelId with the current one
    if (prevModelIdRef.current !== modelId) {
      dispatch(setModel({ id: modelId }));
      setHasModelIdChanged(true);
    } else {
      setHasModelIdChanged(false);
    }

    // Update the ref to the current modelId for the next render
    prevModelIdRef.current = modelId;

    // On unmount, reset the state
    return () => {
      setHasModelIdChanged(false);
    };
  }, [location]); // Effect runs only when location changes

  return { hasModelIdChanged, currentModelId };
};

export const useGetTaxRate = (): number | undefined => {
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );
  const isTemplateMode = !!useTemplateConfigureParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const taxRateParam = queryParams.get('taxRate');
  const isDealerPanel =
    appMode === AppMode.DEALER_PANEL || appMode === AppMode.BUYER;

  if (isDealerPanel && taxRateParam !== null) {
    const parsedTaxRate = parseFloat(taxRateParam);
    if (!isNaN(parsedTaxRate) && parsedTaxRate >= 0 && parsedTaxRate <= 100) {
      return parsedTaxRate;
    }
  }

  if (isTemplateMode) {
    return 0;
  }

  return undefined;
};

// The admin panel tab names the company in the URL. The admin is not acting as
// a company, so there is no logged-in company to infer and no company_mode to
// send — but the backend still requires company_id when there is no intent_uuid.
export const useGetCompanyIdFromUrl = (): number | undefined => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const companyIdParam = queryParams.get('companyId');

  if (companyIdParam === null) {
    return undefined;
  }
  const parsedCompanyId = Number(companyIdParam);
  return isNaN(parsedCompanyId) ? undefined : parsedCompanyId;
};

export const useGetBuilderEditVersionId = (): number | undefined => {
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const editVersionIdParam = queryParams.get('editVersionId');

  if (
    [AppMode.SUPPLIER, AppMode.DEALER_PANEL, AppMode.BUYER].includes(appMode) &&
    editVersionIdParam !== null
  ) {
    const parsedEditVersionId = parseFloat(editVersionIdParam);
    if (!isNaN(parsedEditVersionId)) {
      return parsedEditVersionId;
    }
  }

  return undefined;
};
